<template>
  <section>
    <!-- Хлебные крошки рендерятся здесь (внутри страницы), заголовок статьи приходит из
         onServerPrefetch → корректный SSR для SEO (без клиентского флэша «Пост»). -->
    <Breadcrumbs
      :home-name="{ ru: 'Главная', en: 'Home' }"
      :name-overrides="{ BlogPost: blogpost?.name }"
      class="mn-b-thin"
    />

    <TransitionGroup tag="ul" name="fade" class="o-hidden bg-light radius-medium">
      <SkeletonBlogpost
        v-if="isLoading"
        v-for="i in 3" :key="i"
        class=""
      />
    </TransitionGroup>
    <transition 
      name="fade"
    >
      <EmptyState 
        v-if="!isLoading && !blogpost"
        title="No Blogpost Found"
        description="Currently, there are no blogposts available. Please check back later."
        class="pd-medium mn-b-thin bg-light radius-big"
      />
    </transition>

    <TransitionGroup tag="div" name="fade" class="container">
    	  <!-- <button @click="share" >
			    Поделиться
			  </button>
			   -->
      	<CardBlogpost 
          v-if="!isLoading && blogpost"
          @click="$router.push({name: 'BlogPost', params: {url: blogpost.url}})" 
          :blogpost="blogpost" 
          :user="auth.state.user._id"
          :type="'blogpostPage'"
          :hideTitle="true"
          class="mn-b-small radius-medium mobile:radius-zero"
        >

  	      <Viewer
  	        v-if="blogpost && blogpost.content"
  	        :content="blogpost.content"
  	      />

  				<a v-if="blogpost.source" :href="blogpost.source" target="_blank" class="mn-b-semi d-block w-max radius-big pd-thin bg-black t-white">
  					Check Source 🔗
  				</a>
  			
      </CardBlogpost>

     	<!-- <h3 class="mn-b-small">Recommended Communities</h3>
  		<Publics 
  			class="mn-b-small"
  		/> -->

      <Comments 
        v-if="!isLoading && blogpost" 
        :type="'blogpost'" 
        :target="blogpost._id" 
        :owner="auth.state.user._id"
        class="bg-light radius-medium mobile:radius-zero pd-medium"
      />
      
    </TransitionGroup>  
  
  </section>
</template>

<script setup>
import { ref, onMounted, computed, onServerPrefetch, watch, inject } from 'vue';
import { useHead } from '@unhead/vue'
import Loader from '@martyrs/src/design-system/components/Loader/Loader.vue';
import EmptyState from '@martyrs/src/design-system/components/EmptyState/EmptyState.vue';
import CardBlogpost from '@martyrs/src/modules/community/components/blocks/CardBlogpost.vue';
import SkeletonBlogpost from '@martyrs/src/modules/icons/skeletons/SkeletonBlogpost.vue'
import Publics from '@martyrs/src/modules/organizations/components/sections/Publics.vue'
import Comments from '@martyrs/src/modules/community/components/sections/Comments.vue';
import Viewer from '@martyrs/src/modules/constructor/components/sections/Viewer.vue';
import Breadcrumbs from '@martyrs/src/design-system/components/Breadcrumbs/Breadcrumbs.vue';
import * as blog from '@martyrs/src/modules/community/store/blogposts.js';
import * as auth from '@martyrs/src/modules/auth/views/store/auth.js';
import { useStore } from '@martyrs/src/modules/core/views/store/core.store.js';

import { useRoute } from 'vue-router';

const route = useRoute();
const store = useStore();
const blogpost = computed(() => blog.state.current);
const isLoading = ref(false);
const canShare = ref(false);
const error = ref(null);

async function fetchBlogpost() {
  try {
    isLoading.value = true;
    const data = await blog.read({ user: auth.state.user?._id, url: route.params.url });
    blogpost.value = data[0];
    // Обновляем состояние хранилища
    blog.state.current = data[0];

    // Если пост не найден - устанавливаем 404
    if (!data || data.length === 0 || !blog.state.current) {
      store.core.state.notFound = true;
    }
  } catch (err) {
    error.value = err;
    console.error('Error fetching blogpost:', err);
    store.core.state.notFound = true;
  } finally {
    isLoading.value = false;
  }
}

onServerPrefetch(async() => {
	await fetchBlogpost()
});

onMounted(async() => {
  if (!blogpost.value || blogpost.value.url !== route.params.url) {
    await fetchBlogpost();
  }
  canShare.value = !!navigator.share;
});


const share = async () => {
  if (!canShare.value) return;
  try {
    await navigator.share({
      title: blogpost.value.name,
      text: firstText.value?.content.slice(0, 120) || 'Check out this blog post!',
      url: window.location.href,
    });
    console.log('Content shared successfully');
  } catch (error) {
    console.error('Error sharing:', error);
  }
};

const firstImage = computed(() => {
  return blogpost.value?.content?.find(block => block.type === 'ImageUpload');
});

const firstText = computed(() => {
  return blogpost.value?.content?.find(block => 
    block.type === 'Textarea' && block.class !== 'h2'
  );
});

// Origin приходит из App.vue (provide): assetUrl — CDN/файлы или origin сайта (абсолютный og:image),
// siteUrl — origin страниц (абсолютные URL в JSON-LD). FILE_SERVER_URL как фолбэк для ассетов.
const assetUrl = inject('assetUrl', '');
const siteUrl = inject('siteUrl', '');

const metadata = computed(() => {
  const b = blogpost.value;
  const desc = firstText.value?.content?.slice(0, 160);
  const assetBase = process.env.FILE_SERVER_URL || assetUrl || '';
  const img = `${assetBase}${firstImage.value?.content || '/favicon/cover.jpg'}`;
  const url = b && `${siteUrl}/community/posts/${b.url}`;
  const author = b?.creator?.target?.profile?.name;
  return {
    title: b?.name,
    description: desc,
    meta: [
      { hid: 'description', name: 'description', content: desc },
      { hid: 'og:title', property: 'og:title', content: b?.name },
      { hid: 'og:description', property: 'og:description', content: desc },
      { hid: 'og:image', property: 'og:image', content: img },
      { hid: 'twitter:title', name: 'twitter:title', content: b?.name },
      { hid: 'twitter:description', name: 'twitter:description', content: desc },
      { hid: 'twitter:image', name: 'twitter:image', content: img },
      { hid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
    ],
    script: b && [
      { type: 'application/ld+json', children: JSON.stringify({
        '@context': 'https://schema.org', '@type': 'BlogPosting',
        mainEntityOfPage: url, headline: b.name, description: desc, image: [img],
        datePublished: b.createdAt, dateModified: b.updatedAt || b.createdAt,
        ...(author && { author: { '@type': 'Person', name: author } }),
      }) },
      { type: 'application/ld+json', children: JSON.stringify({
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Community', item: `${siteUrl}/community` },
          { '@type': 'ListItem', position: 2, name: b.name, item: url },
        ],
      }) },
    ],
  };
});

useHead(metadata);
</script>

<style lang="scss">

</style>
